/**
 * Expense queue — IndexedDB persistence for offline-first expenses (#1513).
 *
 * Each entry holds a pending POST payload to /api/associates/expense-logs/
 * plus the receipt as a Blob (so it survives reload / app close). The
 * Expenses page submits through this queue:
 *
 *   1. submit → enqueue (status='pending') + show optimistic row
 *   2. drainQueue() fires immediately AND on `online` / page-visible events
 *   3. on success → drop the entry, refresh server list
 *   4. on 4xx → mark 'failed' (won't retry; user must edit/delete)
 *   5. on 5xx / network → keep 'pending' for the next drain pass
 *
 * Keys are client-generated UUIDs (not autoincrement) so the optimistic UI
 * row has a stable React key from the moment it's enqueued.
 */

import { STORES, get, getAll, getByIndex, put, remove } from './indexedDB';

export type ExpenseQueueStatus = 'pending' | 'syncing' | 'failed';

export interface ExpenseQueueRecord {
  /** Client UUID (used as the optimistic React key + IDB primary key) */
  id: string;
  /** tbl_Expenses.ExpenseID — the category (Mileage, Tolls, Meals, etc.) */
  expenseTypeId: number;
  /** Display name of the type, snapshotted at enqueue so the queued row renders without re-fetching */
  expenseTypeName: string;
  /** YYYY-MM-DD in the rep's local timezone */
  expenseDate: string;
  amount: number;
  description?: string;
  /** Receipt blob — image or PDF. Optional. */
  receiptBlob?: Blob;
  receiptFilename?: string;
  receiptMimeType?: string;
  syncStatus: ExpenseQueueStatus;
  syncAttempts: number;
  lastSyncAttempt?: string;
  syncError?: string;
  createdAt: string; // ISO
}

export const putExpenseQueueEntry = (record: ExpenseQueueRecord): Promise<void> =>
  put(STORES.EXPENSE_LOG_QUEUE, record);

export const getExpenseQueueEntry = (id: string): Promise<ExpenseQueueRecord | undefined> =>
  get<ExpenseQueueRecord>(STORES.EXPENSE_LOG_QUEUE, id);

export const getAllExpenseQueueEntries = (): Promise<ExpenseQueueRecord[]> =>
  getAll<ExpenseQueueRecord>(STORES.EXPENSE_LOG_QUEUE);

export const getPendingExpenseEntries = (): Promise<ExpenseQueueRecord[]> =>
  getByIndex<ExpenseQueueRecord>(STORES.EXPENSE_LOG_QUEUE, 'syncStatus', 'pending');

export const deleteExpenseQueueEntry = (id: string): Promise<void> =>
  remove(STORES.EXPENSE_LOG_QUEUE, id);
